/* eslint-disable max-statements */
import { createSlice } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { createdWorkspace, editedWorkspace, workspacePreferencesFetched, analysesListFetched, createdAnalysisFinished, workspacePreferencesUpdated } from "./api";
import { moduleName } from "./common";
import { workspaceEntityTypeDataToWorkspaceEntityTypeMap } from "./types";
import { language } from "language/default";
import { isDashboardChangedAction } from "store/current-dashboard/utils";
import { getDefaultTemplate } from "store/template";
const initialState = {
    analyses: [],
    dynamicLoadingText: language.en.dashboard.loadingAnalysis,
    isNavigationLoading: false,
    workspacesAndPreferences: []
};
const setWorkspacePreferences = (state, workspaces) => {
    state.workspacesAndPreferences = workspaces.map((item) => ({
        ...item.Workspace,
        isDefault: item.WorkspacePreferences.isDefault,
        isHidden: item.WorkspacePreferences.isHidden,
        orderNumber: item.WorkspacePreferences.orderNumber
    })).sort((a, b) => a.orderNumber - b.orderNumber);
};
export const slice = createSlice({
    extraReducers: (builder) => {
        builder
            .addMatcher(isDashboardChangedAction, (state, action) => {
            if (action?.payload?.location?.pathname) {
                const [, , , workspaceInUrl] = action.payload.location.pathname.split("/");
                if (workspaceInUrl !== undefined && state.currentWorkspace !== workspaceInUrl) {
                    state.analyses = [];
                }
                state.currentWorkspace = workspaceInUrl;
            }
        })
            .addMatcher(workspacePreferencesFetched, (state, { payload: workspaces }) => {
            setWorkspacePreferences(state, workspaces.workspacePreferences);
            if (state.currentWorkspace === undefined) {
                state.currentWorkspace = state.workspacesAndPreferences.find((workspace) => workspace.isDefault === true)?.id;
            }
        })
            .addMatcher(workspacePreferencesUpdated, (state, { payload: workspaces }) => {
            setWorkspacePreferences(state, workspaces.workspacePreferences);
            state.currentWorkspace = state.workspacesAndPreferences.find((workspace) => workspace.isDefault === true)?.id;
        })
            .addMatcher(createdWorkspace, (state, { payload: workspace }) => {
            const orderNumber = state.workspacesAndPreferences.length + 1;
            const newWorkspace = { ...workspace, isDefault: false, isHidden: false, orderNumber };
            state.workspacesAndPreferences.push(newWorkspace);
            state.currentWorkspace = newWorkspace.id;
            state.analyses = [];
        })
            .addMatcher(editedWorkspace, (state, { payload: workspace }) => {
            const indexToEdit = state.workspacesAndPreferences.findIndex((w) => w.id === workspace.id);
            const newCurrentWorkspace = {
                ...workspace,
                isDefault: state.workspacesAndPreferences[indexToEdit].isDefault,
                isHidden: false,
                orderNumber: state.workspacesAndPreferences[indexToEdit].orderNumber
            };
            state.workspacesAndPreferences[indexToEdit] = newCurrentWorkspace;
            state.currentWorkspace = newCurrentWorkspace.id;
        })
            .addMatcher(analysesListFetched, (state, { payload: analysesResponse }) => {
            if (!analysesResponse.analyses.length && analysesResponse.isEditable) {
                state.analyses = [];
                return;
            }
            const { workspaceId } = analysesResponse;
            const workspace = state.workspacesAndPreferences.find((w) => w.id === workspaceId);
            if (workspace?.entityType === undefined) {
                return;
            }
            if (workspace?.isEditable === false) {
                const hasLightPortfolioAnalytics = analysesResponse.hasLightPortfolioAnalytics ?? false;
                const defaultnalyses = getDefaultTemplate(hasLightPortfolioAnalytics);
                const type = workspaceEntityTypeDataToWorkspaceEntityTypeMap(workspace.entityType);
                const template = defaultnalyses[type];
                const analyses = template?.analysis?.map((a, order) => ({
                    applicableAssetClass: a?.applicableAssetClass,
                    applicableAssetClasses: a?.applicableAssetClasses,
                    defaultFilters: a?.defaultFilters,
                    filters: a?.filters,
                    hideWhenMultipleAssetClasses: a?.hideWhenMultipleAssetClasses,
                    id: `${order}-${type}`,
                    isDefault: a?.isDefault,
                    name: a?.name,
                    orderNumber: order,
                    sections: a?.sections,
                    workspaceId
                }));
                state.analyses = analyses;
            }
            else {
                state.analyses = analysesResponse.analyses.map((analysis) => ({
                    ...analysis,
                    sections: analysis.sections
                        ? [...analysis.sections].sort((a, b) => (a.orderNumber ?? 0) - (b.orderNumber ?? 0))
                        : []
                }));
            }
        })
            .addMatcher(createdAnalysisFinished, (state, { payload: createdAnalysis }) => {
            const newAnalysis = createdAnalysis;
            state.analyses?.push(newAnalysis);
        });
    },
    initialState,
    name: moduleName,
    reducers: {
        setCurrentWorkspaceModalType: (state, action) => {
            if (state.currentWorkspaceChanges === undefined) {
                state.currentWorkspaceChanges = { id: state.currentWorkspace, modalType: action.payload };
            }
            else {
                state.currentWorkspaceChanges.modalType = action.payload;
            }
        },
        setCurrentWorkspaceName: (state, action) => {
            const newName = action.payload;
            if (state.currentWorkspaceChanges === undefined) {
                state.currentWorkspaceChanges = { id: state.currentWorkspace, name: newName };
            }
            else {
                state.currentWorkspaceChanges.name = newName;
            }
        },
        setCurrentWorkspacePrivacy: (state, action) => {
            const newIsPrivate = action.payload;
            if (state.currentWorkspaceChanges === undefined) {
                state.currentWorkspaceChanges = { id: state.currentWorkspace, isPrivate: newIsPrivate };
            }
            else {
                state.currentWorkspaceChanges.isPrivate = newIsPrivate;
            }
        },
        setDeleteAnalysis: (state, action) => {
            const filteredAnalyses = state.analyses?.filter((analysis) => analysis.id !== action.payload);
            state.analyses = filteredAnalyses;
        },
        setDynamicText: (state, action) => {
            state.dynamicLoadingText = action.payload;
        },
        setNavigationLoading: (state, action) => {
            state.isNavigationLoading = action.payload;
        },
        setNewWorkspace: (state, action) => {
            const newCurrentWorkspace = { ...action.payload };
            const newWorkspace = action.payload;
            state.workspacesAndPreferences.push(newWorkspace);
            state.currentWorkspace = newCurrentWorkspace.id;
        },
        setWorkspace: (state, action) => {
            if (action.payload) {
                state.currentWorkspace = action.payload;
                state.dynamicLoadingText = language.en.dashboard.loadingAnalysis;
                const currentWorkspace = state.workspacesAndPreferences?.find((workspace) => workspace.id === action.payload?.id);
                if (currentWorkspace?.isDefault === action.payload?.isDefault) {
                    state.analyses = [];
                }
            }
            else {
                const defaultWorkspace = state.workspacesAndPreferences.find((workspace) => workspace.isDefault === true);
                const combinedCurrentWorkspace = { ...defaultWorkspace };
                state.currentWorkspace = combinedCurrentWorkspace.id;
            }
        },
        setWorkspacePreferenceChanges: (state, action) => {
            if (action.payload) {
                state.workspacesAndPreferencesChanges = [...action.payload];
            }
        },
        setWorkspacesAndPreferences: (state, action) => {
            if (action.payload) {
                state.workspacesAndPreferences = [...action.payload];
            }
        }
    }
});
export const workspacesReducers = slice.reducer;
export const { setDeleteAnalysis, setDynamicText, setNavigationLoading, setNewWorkspace, setWorkspace, setWorkspacePreferenceChanges, setCurrentWorkspacePrivacy, setCurrentWorkspaceName, setCurrentWorkspaceModalType, setWorkspacesAndPreferences } = slice.actions;
export const useWorkspacesSelector = useSelector;
